import React, { useState, useEffect, useCallback } from "react";
import {
  Plus,
  Save,
  X,
  CheckCircle,
  FileText,
  Trash2,
  Clock,
  Calendar,
  AlertCircle,
  MessageSquare,
  Target,
  ListChecks,
  Package,
  TrendingUp,
} from "lucide-react";
import Select from "react-select";

const MinutesOfMeetingManager = ({
  enquiryId,
  enquiryStageHistoryId,
  axiosInstance,
  getApiEndpoint,
  onClose,
  readOnly = false,
  showDiscussionTopics = true,
}) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [momData, setMomData] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [contactPersons, setContactPersons] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [error, setError] = useState(null);
  const [productOptions, setProductOptions] = useState([]);

  const [successMessage, setSuccessMessage] = useState(null);

  const [warningPopup, setWarningPopup] = useState({
    isOpen: false,
    title: "",
    message: "",
    type: "",
    data: null,
    onAccept: null,
    onDecline: null,
  });

  const [formData, setFormData] = useState({
    nextMeetOn: "",
    status: "PLANNED",
    topics: [],
    decisions: [],
  });

  // Calculate stats for the fitness-style summary
  const calculateStats = () => {
    const totalTopics = formData.topics.length;
    const totalDecisions = formData.decisions.length;
    const totalActions = formData.topics.reduce(
      (sum, topic) => sum + (topic.actionsResponseDTOList?.length || 0),
      0,
    );
    const completedActions = formData.topics.reduce((sum, topic) => {
      return (
        sum +
        (topic.actionsResponseDTOList?.filter(
          (action) => action.status === "COMPLETED",
        ).length || 0)
      );
    }, 0);
    const totalProducts = formData.topics.reduce((sum, topic) => {
      return sum + (topic.productEnquiryHistoriesIds?.length || 0);
    }, 0);
    const progressPercentage =
      totalActions > 0
        ? Math.round((completedActions / totalActions) * 100)
        : 0;

    return {
      totalTopics,
      totalDecisions,
      totalActions,
      completedActions,
      totalProducts,
      progressPercentage,
    };
  };

  const stats = calculateStats();

  useEffect(() => {
    // Format products for react-select
    const options = availableProducts.map((product) => ({
      value: product.id,
      label: product.name,
    }));
    setProductOptions(options);
  }, [availableProducts]);

  const formatDateTimeLocal = (iso) => {
    if (!iso) return "";
    try {
      const date = new Date(iso);
      const tzOffset = date.getTimezoneOffset() * 60000;
      const localDate = new Date(date.getTime() - tzOffset);
      return localDate.toISOString().slice(0, 16);
    } catch (e) {
      console.error("Error formatting date:", e);
      return "";
    }
  };

  const formatStatusLabel = (status) => {
    if (!status) return "";
    return status
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const formatDateShort = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      });
    } catch (e) {
      return "";
    }
  };

  const showDeleteWarning = (type, data, onAcceptCallback) => {
    const isTopic = type === "topic";
    const title = isTopic ? "Delete Topic" : "Delete Decision";
    const message = isTopic
      ? "Are you sure you want to delete this topic? This action cannot be undone."
      : "Are you sure you want to delete this decision? This action cannot be undone.";

    setWarningPopup({
      isOpen: true,
      title,
      message,
      type,
      data,
      onAccept: onAcceptCallback,
      onDecline: () => setWarningPopup((prev) => ({ ...prev, isOpen: false })),
    });
  };

  const closeWarningPopup = () => {
    setWarningPopup((prev) => ({ ...prev, isOpen: false }));
  };

  const deleteTopicFromBackend = async (topicId) => {
    try {
      const response = await axiosInstance.delete(
        `/api/prod-manager/topic/${topicId}`,
      );
      return response.data;
    } catch (err) {
      console.error("Error deleting topic:", err.response?.data || err.message);
    }
  };

  const deleteDecisionFromBackend = async (decisionId) => {
    try {
      const response = await axiosInstance.delete(
        `/api/prod-manager/decision_taken/${decisionId}`,
      );
      return response.data;
    } catch (err) {
      console.error(
        "Error deleting decision:",
        err.response?.data || err.message,
      );
    }
  };

  // Fetch available products for the enquiry stage
  const fetchAvailableProducts = useCallback(async () => {
    if (!showDiscussionTopics) return;
    try {
      const response = await axiosInstance.get(
        getApiEndpoint(`/enquiry-stage-history/${enquiryStageHistoryId}`),
      );

      if (response.data?.data?.histories?.[0]?.productEnquiryHistories) {
        const products =
          response.data.data.histories[0].productEnquiryHistories.map((p) => ({
            id: p.id,
            name: p.productName || `Product ${p.id}`,
          }));
        setAvailableProducts(products);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
    }
  }, [
    axiosInstance,
    getApiEndpoint,
    enquiryStageHistoryId,
    showDiscussionTopics,
  ]);

  const fetchMOMData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(getApiEndpoint(`/${enquiryId}`));

      if (response.data?.data) {
        const data = response.data.data;
        setMomData(data);

        const topics = (data.topicsResponseDTOList || []).map((t) => ({
          topicId: t.topicId || null,
          topicDetails: t.topicDetails || "",
          contactPersonId: t.contactPersonId || null,
          employeeId: t.employeeId || null,
          contactPersonName: t.contactPersonName || "",
          employeeName: t.employeeName || "",
          productEnquiryHistoriesIds: (
            t.productEnquiryHistoriesResponseDTOS || []
          ).map((p) => p.id),
          actionsResponseDTOList: (t.actionsResponseDTOList || []).map((a) => ({
            actionId: a.actionId || null,
            actions: a.actions || "",
            status: a.status || "PLANNED",
            remarks: a.remarks || "",
          })),
        }));

        const decisions = (data.decisionTakenResponseDTOList || []).map(
          (d) => ({
            decisionTakenId: d.decisionTakenId || null,
            decisions: d.decisions || "",
            status: d.status || "PLANNED",
          }),
        );

        // If no topics exist, add one empty topic
        const finalTopics =
          topics.length === 0
            ? [
                {
                  topicId: null,
                  topicDetails: "",
                  contactPersonId: null,
                  employeeId: null,
                  contactPersonName: "",
                  employeeName: "",
                  productEnquiryHistoriesIds: [],
                  actionsResponseDTOList: [
                    {
                      actionId: null,
                      actions: "",
                      status: "PLANNED",
                      remarks: "",
                    },
                  ],
                },
              ]
            : topics;

        // If no decisions exist, add one empty decision
        const finalDecisions =
          decisions.length === 0
            ? [
                {
                  decisionTakenId: null,
                  decisions: "",
                  status: "PLANNED",
                },
              ]
            : decisions;

        setFormData({
          nextMeetOn: formatDateTimeLocal(data.nextMeetOn),
          status: data.status || "PLANNED",
          topics: finalTopics,
          decisions: finalDecisions,
        });
      } else {
        // No MOM exists - initialize with 1 topic and 1 decision
        setMomData(null);
        setFormData({
          nextMeetOn: "",
          status: "PLANNED",
          topics: [
            {
              topicId: null,
              topicDetails: "",
              contactPersonId: null,
              employeeId: null,
              contactPersonName: "",
              employeeName: "",
              productEnquiryHistoriesIds: [],
              actionsResponseDTOList: [
                { actionId: null, actions: "", status: "PLANNED", remarks: "" },
              ],
            },
          ],
          decisions: [
            {
              decisionTakenId: null,
              decisions: "",
              status: "PLANNED",
            },
          ],
        });
      }
    } catch (err) {
      console.error("Error fetching MOM:", err.response?.data || err.message);
    }
  }, [axiosInstance, getApiEndpoint, enquiryId]);

  const fetchEmployees = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_EMPLOYEE_URL}/employees/list`,
      );
      if (response.data?.data) {
        setEmployees(
          response.data.data.map((emp) => ({
            ...emp,
            fullName: `${emp.firstName || ""} ${emp.lastName || ""}`.trim(),
          })),
        );
      }
    } catch (err) {
      console.error("Error fetching employees:", err);
    }
  }, [axiosInstance]);

  const fetchContactPersons = useCallback(async () => {
    try {
      const response = await axiosInstance.get(
        getApiEndpoint(`/${enquiryId}/contact-person`),
      );
      if (response.data?.data) {
        setContactPersons(response.data.data);
      }
    } catch (err) {
      console.error("Error fetching contact persons:", err);
    }
  }, [axiosInstance, getApiEndpoint, enquiryId]);

  const fetchReadOnlyData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      await fetchMOMData();
    } finally {
      setLoading(false);
    }
  }, [fetchMOMData]);

  const fetchEditableData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const results = await Promise.allSettled([
        fetchMOMData(),
        fetchEmployees(),
        fetchContactPersons(),
        fetchAvailableProducts(),
      ]);

      const rejected = results.find((r) => r.status === "rejected");

      if (rejected) {
        console.error("One or more API calls failed:", rejected.reason);
        setError(
          rejected.reason?.response?.data?.message ||
            "Failed to load some data. Please refresh.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [
    fetchMOMData,
    fetchEmployees,
    fetchContactPersons,
    fetchAvailableProducts,
  ]);

  const createOrUpdateMOM = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccessMessage(null);

      if (!formData.nextMeetOn) {
        setError("Next meeting date is required");
        return;
      }

      const nextMeetOnISO = formData.nextMeetOn
        ? new Date(formData.nextMeetOn).toISOString()
        : null;

      const topicsRequestDTOList = formData.topics.map((topic) => ({
        topicId: topic.topicId || null,
        topicDetails: topic.topicDetails,
        enquiryStageHistoryId: Number(enquiryStageHistoryId),
        contactPersonId: topic.contactPersonId
          ? Number(topic.contactPersonId)
          : null,
        employeeId: topic.employeeId ? Number(topic.employeeId) : null,
        productEnquiryHistoriesIdsList: topic.productEnquiryHistoriesIds.map(
          (id) => Number(id),
        ),
        actionsRequestDTOList: (topic.actionsResponseDTOList || []).map(
          (action) => ({
            actionId: action.actionId || null,
            actions: action.actions,
            status: action.status,
            remarks: action.remarks || "",
          }),
        ),
      }));

      const decisionTakenRequestDTOList = formData.decisions.map(
        (decision) => ({
          decisionTakenId: decision.decisionTakenId || null,
          decisions: decision.decisions,
          status: decision.status,
        }),
      );

      const payload = {
        enquiryId: Number(enquiryId),
        nextMeetOn: nextMeetOnISO,
        status: formData.status,
        topicsRequestDTOList: topicsRequestDTOList,
        decisionTakenRequestDTOList: decisionTakenRequestDTOList,
      };

      console.log("Full payload being sent:", JSON.stringify(payload, null, 2));

      const response = await axiosInstance.post(
        getApiEndpoint("/create/minute_of_meeting"),
        payload,
      );

      console.log("Response:", response.data);

      if (response.data?.status === 200 || response.status === 200) {
        setSuccessMessage("Minutes of Meeting saved successfully!");
        await fetchMOMData();
        setTimeout(() => setSuccessMessage(null), 3000);
      }
    } catch (err) {
      console.error("Error saving MOM:", err.response?.data || err);
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to save Minutes of Meeting. Please try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const addEmptyTopic = () => {
    setFormData((prev) => ({
      ...prev,
      topics: [
        ...prev.topics,
        {
          topicId: null,
          topicDetails: "",
          contactPersonId: null,
          employeeId: null,
          contactPersonName: "",
          employeeName: "",
          productEnquiryHistoriesIds: [],
          actionsResponseDTOList: [
            { actionId: null, actions: "", status: "PLANNED", remarks: "" },
          ],
        },
      ],
    }));
  };

  const updateTopic = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      topics: prev.topics.map((topic, i) =>
        i === index ? { ...topic, [field]: value } : topic,
      ),
    }));
  };

  const updateTopicProducts = (topicIndex, selectedProductIds) => {
    setFormData((prev) => ({
      ...prev,
      topics: prev.topics.map((topic, i) =>
        i === topicIndex
          ? { ...topic, productEnquiryHistoriesIds: selectedProductIds }
          : topic,
      ),
    }));
  };

  const removeTopic = (index) => {
    const topic = formData.topics[index];

    if (topic.topicId) {
      showDeleteWarning("topic", { index, itemId: topic.topicId }, async () => {
        try {
          await deleteTopicFromBackend(topic.topicId);
          setFormData((prev) => ({
            ...prev,
            topics: prev.topics.filter((_, i) => i !== index),
          }));
          closeWarningPopup();
        } catch (err) {
          closeWarningPopup();
        }
      });
    } else {
      setFormData((prev) => ({
        ...prev,
        topics: prev.topics.filter((_, i) => i !== index),
      }));
    }
  };

  const addActionToTopic = (topicIndex) => {
    setFormData((prev) => ({
      ...prev,
      topics: prev.topics.map((topic, i) =>
        i === topicIndex
          ? {
              ...topic,
              actionsResponseDTOList: [
                ...topic.actionsResponseDTOList,
                { actionId: null, actions: "", status: "PLANNED", remarks: "" },
              ],
            }
          : topic,
      ),
    }));
  };

  const updateTopicAction = (topicIndex, actionIndex, field, value) => {
    setFormData((prev) => ({
      ...prev,
      topics: prev.topics.map((topic, i) =>
        i === topicIndex
          ? {
              ...topic,
              actionsResponseDTOList: topic.actionsResponseDTOList.map(
                (action, j) =>
                  j === actionIndex ? { ...action, [field]: value } : action,
              ),
            }
          : topic,
      ),
    }));
  };

  const removeActionFromTopic = (topicIndex, actionIndex) => {
    setFormData((prev) => ({
      ...prev,
      topics: prev.topics.map((topic, i) =>
        i === topicIndex
          ? {
              ...topic,
              actionsResponseDTOList: topic.actionsResponseDTOList.filter(
                (_, j) => j !== actionIndex,
              ),
            }
          : topic,
      ),
    }));
  };

  const addEmptyDecision = () => {
    setFormData((prev) => ({
      ...prev,
      decisions: [
        ...prev.decisions,
        {
          decisionTakenId: null,
          decisions: "",
          status: "PLANNED",
        },
      ],
    }));
  };

  const updateDecision = (index, field, value) => {
    setFormData((prev) => ({
      ...prev,
      decisions: prev.decisions.map((decision, i) =>
        i === index ? { ...decision, [field]: value } : decision,
      ),
    }));
  };

  const removeDecision = (index) => {
    const decision = formData.decisions[index];

    if (decision.decisionTakenId) {
      showDeleteWarning(
        "decision",
        { index, itemId: decision.decisionTakenId },
        async () => {
          try {
            await deleteDecisionFromBackend(decision.decisionTakenId);
            setFormData((prev) => ({
              ...prev,
              decisions: prev.decisions.filter((_, i) => i !== index),
            }));
            closeWarningPopup();
          } catch (err) {
            closeWarningPopup();
          }
        },
      );
    } else {
      setFormData((prev) => ({
        ...prev,
        decisions: prev.decisions.filter((_, i) => i !== index),
      }));
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      PLANNED: "bg-blue-100 text-blue-800",
      IN_PROGRESS: "bg-yellow-100 text-yellow-800",
      COMPLETED: "bg-green-100 text-green-800",
      CANCELLED: "bg-red-100 text-red-800",
    };
    return colors[status] || colors.PLANNED;
  };

  useEffect(() => {
    if (!enquiryId) {
      // Without this, `loading` (initialized true) never gets set back to
      // false — fetchReadOnlyData/fetchEditableData (the only places that
      // call setLoading(false)) never run, so the modal spins on "Loading
      // Minutes of Meeting..." forever with no feedback.
      console.error(
        "MinutesOfMeetingManager: missing enquiryId prop — cannot load MOM data.",
      );
      setError("Unable to load Minutes of Meeting: missing enquiry reference.");
      setLoading(false);
      return;
    }

    if (readOnly) {
      fetchReadOnlyData();
    } else {
      fetchEditableData();
    }
  }, [enquiryId, readOnly, fetchReadOnlyData, fetchEditableData]);

  if (loading && !momData) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-b from-gray-50 to-white">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-sm font-medium text-gray-600">
            Loading Minutes of Meeting...
          </p>
        </div>
      </div>
    );
  }

  if (error && !momData && formData.topics.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[400px] bg-gradient-to-b from-gray-50 to-white">
        <div className="text-center max-w-sm">
          <AlertCircle className="w-10 h-10 text-red-500 mx-auto mb-3" />
          <p className="text-sm font-medium text-gray-700">{error}</p>
          {onClose && (
            <button
              onClick={onClose}
              className="mt-4 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
            >
              Close
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-80px)] bg-white rounded-3xl border border-gray-200">
      {/* Header with Fitness-Style Stats */}
      <div className="shrink-0 sticky top-0 z-10 bg-white backdrop-blur-sm border-b border-gray-200 px-6 py-3">
        <div className="flex flex-col gap-3">
          {/* Main Title Row */}
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2.5 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600">
                <FileText className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900">
                  Minutes of Meeting
                </h1>
                <div className="flex items-center gap-2 mt-1">
                  {formData.nextMeetOn && (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium bg-purple-50 text-purple-700 rounded-lg">
                      <Clock className="w-3 h-3" />
                      Next: {formatDateShort(formData.nextMeetOn)}
                    </span>
                  )}
                  <span
                    className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-lg ${getStatusBadge(
                      formData.status,
                    )}`}
                  >
                    {formatStatusLabel(formData.status)}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {!readOnly && (
                <button
                  onClick={createOrUpdateMOM}
                  disabled={saving}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-600 rounded-[20px] hover:from-green-700 hover:to-green-800 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                >
                  {saving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      Save Changes
                    </>
                  )}
                </button>
              )}
              <button
                onClick={onClose}
                disabled={saving}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-[20px] hover:shadow-lg border border-gray-300 disabled:opacity-50 transition-colors bg-[#ef4444]"
              >
                <X className="w-4 h-4" />
                Close
              </button>
            </div>
          </div>

          {/* Fitness-Style Stats Row */}
          <div className="grid grid-cols-5 gap-2">
            {/* Topics */}
            <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-blue-700">
                    {stats.totalTopics}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <MessageSquare className="w-3 h-3 text-blue-600" />
                    <p className="text-xs font-medium text-blue-600">Topics</p>
                  </div>
                </div>
                <div className="text-xs text-blue-500 font-medium">
                  {stats.totalTopics === 1 ? "Topic" : "Topics"}
                </div>
              </div>
            </div>

            {/* Decisions */}
            <div className="bg-green-50 rounded-xl p-3 border border-green-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-green-700">
                    {stats.totalDecisions}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <ListChecks className="w-3 h-3 text-green-600" />
                    <p className="text-xs font-medium text-green-600">
                      Decisions
                    </p>
                  </div>
                </div>
                <div className="text-xs text-green-500 font-medium">
                  {stats.totalDecisions === 1 ? "Decision" : "Decisions"}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="bg-purple-50 rounded-xl p-3 border border-purple-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-purple-700">
                    {stats.completedActions}/{stats.totalActions}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <Target className="w-3 h-3 text-purple-600" />
                    <p className="text-xs font-medium text-purple-600">
                      Actions
                    </p>
                  </div>
                </div>
                <div className="text-xs text-purple-500 font-medium">
                  {stats.progressPercentage}%
                </div>
              </div>
            </div>

            {/* Products */}
            <div className="bg-amber-50 rounded-xl p-3 border border-amber-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-amber-700">
                    {stats.totalProducts}
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <Package className="w-3 h-3 text-amber-600" />
                    <p className="text-xs font-medium text-amber-600">
                      Products
                    </p>
                  </div>
                </div>
                <div className="text-xs text-amber-500 font-medium">
                  {stats.totalProducts === 1 ? "Product" : "Products"}
                </div>
              </div>
            </div>

            {/* Progress */}
            <div className="bg-cyan-50 rounded-xl p-3 border border-cyan-200">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-2xl font-bold text-cyan-700">
                    {stats.progressPercentage}%
                  </p>
                  <div className="flex items-center gap-1 mt-1">
                    <TrendingUp className="w-3 h-3 text-cyan-600" />
                    <p className="text-xs font-medium text-cyan-600">
                      Progress
                    </p>
                  </div>
                </div>
                <div className="w-8 h-8 rounded-full border-2 border-cyan-300 flex items-center justify-center">
                  <div
                    className="w-6 h-6 rounded-full bg-cyan-500"
                    style={{
                      background: `conic-gradient(#06b6d4 ${stats.progressPercentage}%, #e0f2fe ${stats.progressPercentage}%)`,
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      {(error || successMessage) && (
        <div className="shrink-0 px-6 py-3">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600" />
              <p className="text-sm font-medium text-red-800">{error}</p>
              <button
                onClick={() => setError(null)}
                className="ml-auto text-red-500"
              >
                <X size={18} />
              </button>
            </div>
          )}
          {successMessage && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-xl flex items-start gap-3">
              <CheckCircle className="w-5 h-5 text-green-600" />
              <p className="text-sm font-medium text-green-800">
                {successMessage}
              </p>
              <button
                onClick={() => setSuccessMessage(null)}
                className="ml-auto text-green-500"
              >
                <X size={18} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Main Content */}
      <div className="flex-1 overflow-auto px-6 py-4">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Left Column - Meeting Details */}
          <div
            className={
              !showDiscussionTopics
                ? "lg:col-span-3 grid grid-cols-1 lg:grid-cols-2 gap-4 items-start"
                : "space-y-6"
            }
          >
            {/* Meeting Details */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-blue-50 to-blue-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center gap-2.5">
                  <Calendar className="w-4 h-4 text-blue-600" />
                  <h2 className="text-base font-bold text-gray-900">
                    Meeting Details
                  </h2>
                </div>
              </div>
              <div className="p-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Next Meeting
                  </label>
                  <input
                    type="datetime-local"
                    value={formData.nextMeetOn}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        nextMeetOn: e.target.value,
                      }))
                    }
                    disabled={readOnly}
                    className="w-full px-3 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Meeting Status
                  </label>
                  <div className="grid grid-cols-4 gap-2">
                    {[
                      { value: "PLANNED", label: "Planned" },
                      { value: "IN_PROGRESS", label: "In Progress" },
                      { value: "COMPLETED", label: "Completed" },
                      { value: "CANCELLED", label: "Cancelled" },
                    ].map((status) => {
                      const isActive = formData.status === status.value;

                      const colors = {
                        PLANNED: "bg-blue-500 hover:bg-blue-600",
                        IN_PROGRESS: "bg-yellow-500 hover:bg-yellow-600",
                        COMPLETED: "bg-green-500 hover:bg-green-600",
                        CANCELLED: "bg-red-500 hover:bg-red-600",
                      };

                      return (
                        <button
                          key={status.value}
                          type="button"
                          disabled={readOnly}
                          onClick={() =>
                            setFormData((prev) => ({
                              ...prev,
                              status: status.value,
                            }))
                          }
                          className={`px-3 py-2 text-sm font-medium rounded-xl transition-all 
        ${
          isActive
            ? `${colors[status.value]} text-white shadow-md`
            : "bg-gray-100 text-gray-600 hover:bg-gray-200"
        }
        ${readOnly ? "opacity-50 cursor-not-allowed" : ""}`}
                        >
                          {status.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Decisions Section */}
            <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
              <div className="bg-gradient-to-r from-green-50 to-green-50 px-4 py-3 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <ListChecks className="w-4 h-4 text-green-600" />
                    <h2 className="text-base font-bold text-gray-900">
                      Decisions Taken
                    </h2>
                  </div>
                  {!readOnly && (
                    <button
                      onClick={addEmptyDecision}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-green-600 hover:bg-green-50 rounded-lg"
                    >
                      <Plus className="w-4 h-4" />
                      Add Decision
                    </button>
                  )}
                </div>
              </div>

              <div className="p-4">
                {formData.decisions.length > 0 ? (
                  <div className="space-y-3">
                    {formData.decisions.map((decision, decisionIndex) => (
                      <div
                        key={decisionIndex}
                        className="bg-gray-50 rounded-xl border border-gray-200 p-3"
                      >
                        <div className="flex items-start justify-between gap-2 mb-3">
                          <textarea
                            value={decision.decisions}
                            onChange={(e) =>
                              updateDecision(
                                decisionIndex,
                                "decisions",
                                e.target.value,
                              )
                            }
                            disabled={readOnly}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 resize-y min-h-[100px]"
                            rows="5"
                            placeholder="Enter decision details..."
                          />
                          {!readOnly && (
                            <button
                              onClick={() => removeDecision(decisionIndex)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                        <select
                          value={decision.status}
                          onChange={(e) =>
                            updateDecision(
                              decisionIndex,
                              "status",
                              e.target.value,
                            )
                          }
                          className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-green-500"
                        >
                          <option value="PLANNED">Planned</option>
                          <option value="IN_PROGRESS">In Progress</option>
                          <option value="COMPLETED">Completed</option>
                        </select>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <ListChecks className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                    <p className="text-sm font-medium text-gray-600">
                      No decisions recorded
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Middle & Right Columns - Topics (2/3 width) */}
          {showDiscussionTopics && (
            <div className="lg:col-span-2 space-y-6">
              {/* Topics Section */}
              <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-blue-50 to-blue-50 px-4 py-3 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                      <MessageSquare className="w-4 h-4 text-blue-600" />
                      <h2 className="text-base font-bold text-gray-900">
                        Discussion Topics
                      </h2>
                    </div>
                    {!readOnly && (
                      <button
                        onClick={addEmptyTopic}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-50 rounded-lg"
                      >
                        <Plus className="w-4 h-4" />
                        Add Topic
                      </button>
                    )}
                  </div>
                </div>

                <div className="p-4 space-y-3">
                  {formData.topics.map((topic, topicIndex) => (
                    <div
                      key={topicIndex}
                      className="bg-gray-50 rounded-xl border border-gray-200 p-4"
                    >
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <textarea
                          value={topic.topicDetails}
                          onChange={(e) =>
                            updateTopic(
                              topicIndex,
                              "topicDetails",
                              e.target.value,
                            )
                          }
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 resize-none"
                          rows="2"
                          placeholder="Enter topic details..."
                        />
                        <button
                          onClick={() => removeTopic(topicIndex)}
                          className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      {/* Contact Person & Employee */}
                      <div className="grid grid-cols-2 gap-3 mb-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Client/Lead Person
                          </label>
                          <select
                            value={topic.contactPersonId || ""}
                            onChange={(e) =>
                              updateTopic(
                                topicIndex,
                                "contactPersonId",
                                e.target.value,
                              )
                            }
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
                          >
                            <option value="">Select contact</option>
                            {contactPersons.map((cp) => (
                              <option key={cp.id} value={cp.id}>
                                {cp.contactPersonName}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">
                            Discussed With
                          </label>
                          <select
                            value={topic.employeeId || ""}
                            onChange={(e) =>
                              updateTopic(
                                topicIndex,
                                "employeeId",
                                e.target.value,
                              )
                            }
                            className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg"
                          >
                            <option value="">Select employee</option>
                            {employees.map((emp) => (
                              <option key={emp.empId} value={emp.empId}>
                                {emp.fullName}
                              </option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Products Multi-Select */}
                      <div className="mb-3">
                        <label className="block text-xs font-medium text-gray-700 mb-1">
                          <Package className="w-3 h-3 inline mr-1" />
                          Related Products
                        </label>

                        <Select
                          isMulti
                          options={productOptions}
                          value={productOptions.filter((option) =>
                            topic.productEnquiryHistoriesIds.includes(
                              option.value,
                            ),
                          )}
                          onChange={(selectedOptions) => {
                            const selectedIds = selectedOptions.map(
                              (option) => option.value,
                            );
                            updateTopicProducts(topicIndex, selectedIds);
                          }}
                          placeholder="Select products..."
                          className="react-select-container"
                          classNamePrefix="react-select"
                          menuPortalTarget={document.body}
                          menuPosition="fixed"
                          styles={{
                            control: (base) => ({
                              ...base,
                              borderColor: "#d1d5db",
                              borderRadius: "0.5rem",
                              minHeight: "2.5rem",
                              fontSize: "0.875rem",
                              "&:hover": {
                                borderColor: "#9ca3af",
                              },
                            }),
                            menu: (base) => ({
                              ...base,
                              fontSize: "0.875rem",
                            }),
                            menuPortal: (base) => ({
                              ...base,
                              zIndex: 9999,
                            }),
                            multiValue: (base) => ({
                              ...base,
                              backgroundColor: "#dbeafe",
                            }),
                            multiValueLabel: (base) => ({
                              ...base,
                              color: "#1e40af",
                            }),
                          }}
                        />
                      </div>

                      {/* Actions */}
                      <div className="pl-2 border-l-2 border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <Target className="w-4 h-4 text-blue-500" />
                            <p className="text-xs font-semibold text-gray-700">
                              Actions ({topic.actionsResponseDTOList.length})
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={() => addActionToTopic(topicIndex)}
                            className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5"
                          >
                            <Plus className="w-3 h-3" />
                            Add Action
                          </button>
                        </div>

                        <div className="space-y-2">
                          {topic.actionsResponseDTOList.map(
                            (action, actionIndex) => (
                              <div
                                key={actionIndex}
                                className="bg-white rounded-lg border border-gray-200 p-3"
                              >
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 space-y-2">
                                    <input
                                      type="text"
                                      value={action.actions}
                                      onChange={(e) =>
                                        updateTopicAction(
                                          topicIndex,
                                          actionIndex,
                                          "actions",
                                          e.target.value,
                                        )
                                      }
                                      placeholder="Action description..."
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                    />
                                    <input
                                      type="text"
                                      value={action.remarks}
                                      onChange={(e) =>
                                        updateTopicAction(
                                          topicIndex,
                                          actionIndex,
                                          "remarks",
                                          e.target.value,
                                        )
                                      }
                                      placeholder="Remarks..."
                                      className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <select
                                      value={action.status}
                                      onChange={(e) =>
                                        updateTopicAction(
                                          topicIndex,
                                          actionIndex,
                                          "status",
                                          e.target.value,
                                        )
                                      }
                                      className="px-2 py-1 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500"
                                    >
                                      <option value="PLANNED">Planned</option>
                                      <option value="IN_PROGRESS">
                                        In Progress
                                      </option>
                                      <option value="COMPLETED">
                                        Completed
                                      </option>
                                    </select>
                                    <button
                                      onClick={() =>
                                        removeActionFromTopic(
                                          topicIndex,
                                          actionIndex,
                                        )
                                      }
                                      className="p-1 text-red-600 hover:bg-red-50 rounded-lg"
                                    >
                                      <Trash2 className="w-3 h-3" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            ),
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Warning Popup */}
      {warningPopup.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start gap-4">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-gray-900 mb-2">
                  {warningPopup.title}
                </h3>
                <p className="text-sm text-gray-600">{warningPopup.message}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 mt-6">
              <button
                onClick={warningPopup.onDecline}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={warningPopup.onAccept}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-xl transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MinutesOfMeetingManager;
